import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from "react";
import {
  FormGroup,
  FormHelperText,
  InputAdornment,
  SxProps,
  TextField,
} from "@mui/material";

import { formInputsStyles } from "../../Configuration/styles";

export const CustomTextField = ({
  identifier,
  label,
  defaultValue,
  required = true,
  description = undefined,
  placeholder,
  value,
  onChange,
  onBlur,
  onKeyDown,
  isNested = false,
  disabled = false,
  multiline = false,
  minRows,
  maxRows,
  size,
  autoFocus,
  sx,
  type,
  min,
  step,
  endAdornment,
}: {
  identifier?: string;
  label?: string | ReactNode;
  defaultValue?: string;
  required?: boolean;
  description?: string;
  placeholder?: string;
  value?: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  isNested?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  size?: "small" | "medium";
  autoFocus?: boolean;
  sx?: SxProps;
  type?: "text" | "number" | "email" | "password";
  min?: number;
  // Native `step` of a number input; "any" lifts the browser's integer-only
  // default so decimals (prices, rates) pass form validation.
  step?: number | "any";
  endAdornment?: ReactNode;
}) => {
  const { classes: formClasses } = formInputsStyles();
  const inputLabel = label
    ? label
    : identifier
    ? identifier.charAt(0).toUpperCase() + identifier.slice(1)
    : undefined;

  return (
    <FormGroup>
      <TextField
        className={`${formClasses.inputText} ${
          isNested ? formClasses.connectivityInputReversed : ""
        }`}
        sx={sx}
        required={required}
        id={identifier}
        label={inputLabel}
        defaultValue={defaultValue}
        placeholder={placeholder ?? description}
        fullWidth
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={
          type === "number"
            ? (e) => {
                const preventMinus = min !== undefined && min >= 0;
                if (
                  ["e", "E", "+"].includes(e.key) ||
                  (preventMinus && e.key === "-")
                ) {
                  e.preventDefault();
                }
                onKeyDown?.(e);
              }
            : onKeyDown
        }
        value={value}
        disabled={disabled}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        size={size}
        autoFocus={autoFocus}
        type={type}
        slotProps={{
          input:
            endAdornment !== undefined
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      {endAdornment}
                    </InputAdornment>
                  ),
                }
              : undefined,
          htmlInput:
            min !== undefined || step !== undefined ? { min, step } : undefined,
        }}
      />
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormGroup>
  );
};
